import { HttpClient } from '@angular/common/http'
import { Environment } from '@fabrik/common'
import { Observable, forkJoin, of } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'

import { Checkin, CheckinsResponse, CheckinLocationOption } from '../models/Checkin'
import { TimeUtils } from '../../common/utilities/time-utils'
import moment from 'moment'

export class CheckinsDataService {
    constructor(private environment: Environment, private http: HttpClient) {}

    getCheckins(tenantId: string, startDate?: Date, endDate?: Date, locationId?: string | null): Observable<Checkin[]> {
        const limit = 20
        let page = 0
        const allCheckins: Checkin[] = []

        const buildUrl = (currentPage: number): string => {
            const params: string[] = [`tenantId=${tenantId}`, `limit=${limit}`, `page=${currentPage}`]

            if (startDate && endDate) {
                params.push(`startDate=${moment(startDate).utc().toISOString()}`)
                params.push(`endDate=${moment(endDate).utc().toISOString()}`)
            }

            if (locationId) {
                params.push(`locationId=${locationId}`)
            }

            return `${this.environment.appApiHost}/api/${tenantId}/admin/directory/tenantCheckins?${params.join('&')}`
        }

        const fetchPage = (currentPage: number): Observable<CheckinsResponse> => {
            return this.http.get<CheckinsResponse>(buildUrl(currentPage))
        }

        return fetchPage(page).pipe(
            switchMap((firstResponse) => {
                allCheckins.push(...firstResponse.result)

                const totalPages = Math.ceil(firstResponse.totalCount / limit)
                const remainingPages: Observable<CheckinsResponse>[] = []

                for (let i = 1; i < totalPages; i++) {
                    remainingPages.push(fetchPage(i))
                }

                if (remainingPages.length === 0) {
                    return of(allCheckins)
                }

                return forkJoin(remainingPages).pipe(
                    map((responses) => {
                        responses.forEach((response) => {
                            allCheckins.push(...response.result)
                        })
                        return allCheckins
                    })
                )
            })
        )
    }

    getCheckinsPaginated(tenantId: string, limit: number, page: number, startDate?: Date, endDate?: Date, locationId?: string | null): Observable<CheckinsResponse> {
        const params: string[] = [`tenantId=${tenantId}`, `limit=${limit}`, `page=${page}`]

        if (startDate && endDate) {
            params.push(`startDate=${moment(startDate).utc().toISOString()}`)
            params.push(`endDate=${moment(endDate).utc().toISOString()}`)
        }

        if (locationId) {
            params.push(`locationId=${locationId}`)
        }

        const url = `${this.environment.appApiHost}/api/${tenantId}/admin/directory/tenantCheckins?${params.join('&')}`
        return this.http.get<CheckinsResponse>(url)
    }

    getCheckinLocations(tenantId: string): Observable<CheckinLocationOption[]> {
        return this.http.get<CheckinLocationOption[]>(`${this.environment.appApiHost}/api/${tenantId}/directory/checkins?api-version=1.21.0`)
    }
}

